import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Pencil, FileText, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { TimeSelect } from '../ui/TimeSelect';

const PROCEDURE_LABELS = {
  heart_scan: 'Heart Scan', measurement: 'Measurement', physical: 'Physical',
  scope_in_person: 'Scope - In Person', scope_repository: 'Scope - Repository',
  ultrasound: 'Ultrasound', xray_in_person: 'X-ray - In Person',
  xray_repository: 'X-ray - Repository', other: 'Other',
};

const fmtDate = (d) => d
  ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '—';

// Parse ISO datetime → { date_part: "YYYY-MM-DD", time_part: "HH:MM" }
const parseDT = (dt) => {
  if (!dt) return { date_part: '', time_part: '' };
  const d = new Date(dt);
  if (isNaN(d)) return { date_part: '', time_part: '' };
  return {
    date_part: d.toISOString().slice(0, 10),
    time_part: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
  };
};

// Combine back to ISO string for saving
const buildDT = (date_part, time_part) => {
  if (!date_part || !time_part) return null;
  return `${date_part}T${time_part}:00`;
};

const VettingProcedureDetailDialog = ({ procedure, onClose, onSaved }) => {
  const { isViewOnly } = useAuth();
  const viewOnly = isViewOnly();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // CRM search state
  const [allContacts, setAllContacts] = useState([]);
  const [vetSearch, setVetSearch] = useState('');
  const [practiceSearch, setPracticeSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [vetAssocPractices, setVetAssocPractices] = useState([]);
  const [quickCreate, setQuickCreate] = useState(null);
  const [newName, setNewName] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newType, setNewType] = useState('person');
  const [newOrgName, setNewOrgName] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [quickSaving, setQuickSaving] = useState(false);

  useEffect(() => {
    if (procedure) {
      setEditing(false);
      const { date_part, time_part } = parseDT(procedure.procedure_date_time);
      setForm({
        vet_crm_entity_id: procedure.vet_crm_entity_id || null,
        vet_name: procedure.vet_name || '',
        vet_practice_crm_entity_id: procedure.vet_practice_crm_entity_id || null,
        vet_practice: procedure.vet_practice || '',
        client_crm_entity_id: procedure.client_crm_entity_id || null,
        client_name: procedure.client_name || '',
        procedure: procedure.procedure || '',
        date_part,
        time_part,
        viewed_vet_report: procedure.viewed_vet_report || false,
        notes: procedure.notes || '',
        time_spent_watching: procedure.time_spent_watching || '',
      });
      setVetSearch(''); setPracticeSearch(''); setClientSearch('');
      setVetAssocPractices([]); setQuickCreate(null);
    }
  }, [procedure]);

  useEffect(() => {
    if (editing && allContacts.length === 0) {
      api.get('/crm-entities').then(r => setAllContacts(r.data || [])).catch(() => {});
    }
  }, [editing]);

  const vets = allContacts.filter(c => c.person_industry_role === 'veterinarian' || c.person_industry_role === 'vet');
  const vetPractices = allContacts.filter(c => c.organization_industry_role === 'veterinary_care');
  const filteredVets = vetSearch.length > 0 ? vets.filter(v => v.system_name?.toLowerCase().includes(vetSearch.toLowerCase())) : vets;
  const filteredPractices = vetAssocPractices.length > 0
    ? vetAssocPractices
    : (practiceSearch.length > 0 ? vetPractices.filter(v => v.system_name?.toLowerCase().includes(practiceSearch.toLowerCase())) : vetPractices);
  const filteredClients = clientSearch.length > 0
    ? allContacts.filter(c => c.system_name?.toLowerCase().includes(clientSearch.toLowerCase())).slice(0, 10)
    : [];

  const selectVet = async (entity) => {
    setForm(f => ({ ...f, vet_crm_entity_id: entity.id, vet_name: entity.system_name }));
    setVetSearch('');
    try {
      const assocRes = await api.get(`/crm-entities/${entity.id}/associations`);
      const assocs = assocRes.data || [];
      const practiceIds = assocs.map(a => a.crm_entity_1_id === entity.id ? a.crm_entity_2_id : a.crm_entity_1_id);
      const practices = allContacts.filter(c => practiceIds.includes(c.id) && c.organization_industry_role === 'veterinary_care');
      setVetAssocPractices(practices);
      if (practices.length === 1) {
        setForm(f => ({ ...f, vet_practice_crm_entity_id: practices[0].id, vet_practice: practices[0].system_name }));
      }
    } catch { setVetAssocPractices([]); }
  };

  const handleQuickCreate = async () => {
    setQuickSaving(true);
    try {
      if (quickCreate === 'vet') {
        if (!newName || !newLast) { toast.error('First and last name required'); setQuickSaving(false); return; }
        const res = await api.post('/crm-entities', { crm_type: 'person', crm_class: 'contact', person_first_name: newName, person_last_name: newLast, person_industry_role: 'veterinarian', system_name: `${newName} ${newLast}` });
        setForm(f => ({ ...f, vet_crm_entity_id: res.data.id, vet_name: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Veterinarian created');
      } else if (quickCreate === 'practice') {
        if (!newName) { toast.error('Practice name required'); setQuickSaving(false); return; }
        const res = await api.post('/crm-entities', { crm_type: 'organization', crm_class: 'contact', organization_name: newName, organization_industry_role: 'veterinary_care', system_name: newName });
        setForm(f => ({ ...f, vet_practice_crm_entity_id: res.data.id, vet_practice: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Practice created');
      } else if (quickCreate === 'client') {
        const isOrg = newType === 'organization';
      const displayName = isOrg ? newOrgName.trim() : `${newName} ${newLast}`.trim();
      if (isOrg && !newOrgName) { toast.error('Organization name required'); setQuickSaving(false); return; }
      if (!isOrg && (!newName || !newLast)) { toast.error('First and last name required'); setQuickSaving(false); return; }
      const payload = isOrg
        ? { crm_type: 'organization', crm_class: 'contact', organization_name: newOrgName, system_name: newOrgName }
        : { crm_type: 'person', crm_class: 'contact', person_first_name: newName, person_last_name: newLast, system_name: displayName };
        const res = await api.post('/crm-entities', payload);
        setForm(f => ({ ...f, client_crm_entity_id: res.data.id, client_name: res.data.system_name }));
        setAllContacts(prev => [...prev, res.data]);
        toast.success('Client created');
      }
    } catch { toast.error('Failed to create'); }
    setQuickCreate(null); setNewName(''); setNewLast('');
    setQuickSaving(false);
  };

  const CrmSearchField = ({ label, value, search, onSearch, results, onSelect, onClear, createType, placeholder, fieldKey, allItems }) => {
    const displayResults = focusedField === fieldKey ? (search.length > 0 ? results : (allItems || results).slice(0, 8)) : [];
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        {value ? (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{value}</Badge>
            <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={onClear}>Change</Button>
          </div>
        ) : quickCreate === createType ? (
          <div className="space-y-2 p-2 border rounded">
            <div className="flex gap-2">
              {createType === 'practice' ? (
                <Input placeholder="Practice Name *" value={newName} onChange={(e) => setNewName(e.target.value)} className="text-sm" />
              ) : (
                <>
                  <Input placeholder="First Name *" value={newName} onChange={(e) => setNewName(e.target.value)} className="text-sm" />
                  <Input placeholder="Last Name *" value={newLast} onChange={(e) => setNewLast(e.target.value)} className="text-sm" />
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleQuickCreate} disabled={quickSaving} className="bg-primary hover:bg-primary/90 text-white text-xs">Create</Button>
              <Button size="sm" variant="outline" onClick={() => setQuickCreate(null)} className="text-xs">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                className="pl-8 h-8 text-sm"
                placeholder={placeholder}
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                onFocus={() => setFocusedField(fieldKey)}
                onBlur={() => setTimeout(() => setFocusedField(null), 200)}
              />
            </div>
            {displayResults.length > 0 && (
              <div className="border rounded max-h-24 overflow-y-auto">
                {displayResults.slice(0, 6).map(e => (
                  <div key={e.id} className="p-1.5 text-xs hover:bg-muted/50 cursor-pointer" onClick={() => onSelect(e)}>
                    {e.system_name}
                  </div>
                ))}
              </div>
            )}
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => { setQuickCreate(createType); setNewName(''); setNewLast(''); if (createType === 'client') { setNewType('person'); setNewOrgName(''); } }}>
              + Create New
            </Button>
          </div>
        )}
      </div>
    );
  };

  const handleSave = async () => {
    const procedure_date_time = buildDT(form.date_part, form.time_part);
    setSaving(true);
    try {
      await api.put(`/vetting-procedures/${procedure.id}`, { ...form, procedure_date_time });
      toast.success('Procedure updated');
      setEditing(false);
      if (onSaved) onSaved(procedure.id);
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const isRepositoryScope = (form.procedure || '') === 'scope_repository';
  const timeComplete = !!(form.date_part && form.time_part);

  return (
    <Dialog open={!!procedure} onOpenChange={(v) => { if (!v) { setEditing(false); onClose(); } }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Vetting Procedure
            {procedure?.horse_name && (
              <span className="text-sm font-normal text-muted-foreground">
                — {procedure.hip_number ? `#${procedure.hip_number} ` : ''}{procedure.horse_name}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {!editing ? (
          /* ── VIEW MODE ── */
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Vet</p>
                <p className="font-medium">{procedure?.vet_name || '—'}</p>
                {procedure?.vet_practice && <p className="text-xs text-muted-foreground">{procedure.vet_practice}</p>}
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Client</p>
                <p className="font-medium">{procedure?.client_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Procedure</p>
                <p className="font-medium">{PROCEDURE_LABELS[procedure?.procedure] || procedure?.procedure || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Date / Time</p>
                <p className="font-medium">{fmtDate(procedure?.procedure_date_time)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Viewed Vet Report</p>
                {procedure?.viewed_vet_report
                  ? <Badge className="bg-blue-100 text-blue-700 pointer-events-none"><FileText className="w-3 h-3 mr-1 inline" />Yes</Badge>
                  : <span className="text-muted-foreground">No</span>}
              </div>
              {procedure?.procedure === 'scope_repository' && procedure?.time_spent_watching && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Time Watching (sec)</p>
                  <p className="font-medium">{procedure.time_spent_watching}</p>
                </div>
              )}
            </div>
            {procedure?.notes && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
                <p className="text-sm">{procedure.notes}</p>
              </div>
            )}
          </div>
        ) : (
          /* ── EDIT MODE ── */
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <CrmSearchField
                label="Vet Name" value={form.vet_name} search={vetSearch} onSearch={setVetSearch}
                results={filteredVets} onSelect={selectVet}
                onClear={() => { setForm(f => ({ ...f, vet_crm_entity_id: null, vet_name: '', vet_practice_crm_entity_id: null, vet_practice: '' })); setVetAssocPractices([]); }}
                createType="vet" placeholder="Search vets..." fieldKey="vet"
              />
              <CrmSearchField
                label="Vet Practice" value={form.vet_practice} search={practiceSearch} onSearch={setPracticeSearch}
                results={filteredPractices} onSelect={(e) => { setForm(f => ({ ...f, vet_practice_crm_entity_id: e.id, vet_practice: e.system_name })); setPracticeSearch(''); }}
                onClear={() => setForm(f => ({ ...f, vet_practice_crm_entity_id: null, vet_practice: '' }))}
                createType="practice" placeholder="Search practices..." fieldKey="practice"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* Client — card-form-style Person/Org quick-create */}
              <div className="space-y-2">
                <Label>Client</Label>
                {form.client_name ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{form.client_name}</Badge>
                    <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => setForm(f => ({ ...f, client_crm_entity_id: null, client_name: '' }))}>Change</Button>
                  </div>
                ) : quickCreate === 'client' ? (
                  <div className="space-y-2 p-2 border rounded">
                    <div className="flex gap-1">
                      <Button size="sm" type="button" variant={newType === 'person' ? 'default' : 'outline'} className="h-6 text-xs flex-1" onClick={() => setNewType('person')}>Person</Button>
                      <Button size="sm" type="button" variant={newType === 'organization' ? 'default' : 'outline'} className="h-6 text-xs flex-1" onClick={() => setNewType('organization')}>Organization</Button>
                    </div>
                    {newType === 'person' ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="First Name *" value={newName} onChange={e => setNewName(e.target.value)} className="text-sm" />
                        <Input placeholder="Last Name *" value={newLast} onChange={e => setNewLast(e.target.value)} className="text-sm" />
                      </div>
                    ) : (
                      <Input placeholder="Organization Name *" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} className="text-sm" />
                    )}
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleQuickCreate} disabled={quickSaving} className="bg-primary hover:bg-primary/90 text-white text-xs">Create</Button>
                      <Button size="sm" variant="outline" onClick={() => setQuickCreate(null)} className="text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input className="pl-8 h-8 text-sm" placeholder="Search clients..." value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                        onFocus={() => setFocusedField('client')} onBlur={() => setTimeout(() => setFocusedField(null), 200)} />
                    </div>
                    {focusedField === 'client' && filteredClients.slice(0, 6).length > 0 && (
                      <div className="border rounded max-h-24 overflow-y-auto">
                        {filteredClients.slice(0, 6).map(e => (
                          <div key={e.id} className="p-1.5 text-xs hover:bg-muted/50 cursor-pointer" onClick={() => { setForm(f => ({ ...f, client_crm_entity_id: e.id, client_name: e.system_name })); setClientSearch(''); }}>
                            {e.system_name}
                          </div>
                        ))}
                      </div>
                    )}
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs"
                      onClick={() => { setQuickCreate('client'); setNewName(''); setNewLast(''); setNewOrgName(''); setNewType('person'); }}>
                      + Create New
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Procedure</Label>
                <Select value={form.procedure} onValueChange={(v) => setForm({ ...form, procedure: v })}>
                  <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PROCEDURE_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date + TimeSelect (same as Edit Session) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Date / Time
                {!timeComplete && <span className="text-xs text-amber-600 font-normal">(incomplete)</span>}
              </Label>
              <div className="flex gap-2 flex-wrap items-center">
                <Input
                  type="date"
                  value={form.date_part || ''}
                  onChange={(e) => setForm(f => ({ ...f, date_part: e.target.value }))}
                  className={`w-36 h-9 text-sm ${form.date_part ? 'date-filled' : ''}`}
                />
                <TimeSelect
                  value={form.time_part || ''}
                  onChange={(v) => setForm(f => ({ ...f, time_part: v }))}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="detail-viewed-vet"
                checked={form.viewed_vet_report}
                onChange={(e) => setForm({ ...form, viewed_vet_report: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="detail-viewed-vet">Viewed Vet Report</Label>
            </div>

            {isRepositoryScope && (
              <div className="space-y-2">
                <Label>Time Spent Watching (seconds)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.time_spent_watching}
                  onChange={(e) => setForm({ ...form, time_spent_watching: parseInt(e.target.value) || '' })}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any notes..." />
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-end items-center mt-2">
          {!editing && !viewOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute bottom-4 left-4 text-muted-foreground hover:text-primary"
              onClick={() => setEditing(true)}
              data-testid="vetting-detail-edit-btn"
              title="Edit procedure"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          {editing && (
            <Button variant="ghost" size="sm" className="absolute bottom-4 left-4 text-muted-foreground" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          )}
          <div>
            {editing ? (
              <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            ) : (
              <Button variant="outline" onClick={onClose}>Close</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VettingProcedureDetailDialog;
